/**
 * KOAI SaaS 사이드바 메뉴 설정
 * RBAC: plan(free|paid) + role(user|admin)
 */

import {
  Dashboard as DashboardIcon,
  Announcement as AnnouncementIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Extension as ExtensionIcon,
  AutoAwesome as PromptClinicIcon,
  AccountTree as DeciCraftIcon,
  Gavel as DecisionIcon,
  Person as MyPageIcon,
  MenuBook as DocsIcon,
} from '@mui/icons-material';

/**
 * adminOnly: 플랫폼 관리자(role=admin)만
 * minLevel: 레거시 호환(숫자). SaaS에서는 adminOnly 우선
 */
export const menuItems = [
  {
    key: 'dashboard',
    path: '/dashboard',
    icon: DashboardIcon,
    minLevel: 1,
    labelKey: 'nav.dashboard',
    section: 'main'
  },
  {
    key: 'notices',
    path: '/notices',
    icon: AnnouncementIcon,
    minLevel: 1,
    labelKey: 'nav.notices',
    section: 'main'
  },
  {
    key: 'chat',
    path: '/chat',
    icon: ChatIcon,
    minLevel: 1,
    labelKey: 'nav.chat',
    section: 'main'
  },
  {
    key: 'mypage',
    path: '/mypage',
    icon: MyPageIcon,
    minLevel: 1,
    labelKey: 'nav.mypage',
    section: 'admin'
  },
  {
    key: 'users',
    path: '/users',
    icon: PeopleIcon,
    minLevel: 5,
    adminOnly: true,
    labelKey: 'nav.users',
    section: 'admin'
  },
  {
    key: 'platform',
    path: '/platform',
    icon: AdminIcon,
    minLevel: 5,
    adminOnly: true,
    labelKey: 'nav.platform',
    section: 'admin'
  },
  {
    key: 'settings',
    path: '/settings',
    icon: SettingsIcon,
    minLevel: 5,
    adminOnly: true,
    labelKey: 'nav.settings',
    section: 'admin'
  },
  {
    key: 'extensions',
    path: '/extensions',
    icon: ExtensionIcon,
    minLevel: 1,
    labelKey: 'nav.extensions',
    expandable: true,
    children: [
      {
        key: 'promptclinic',
        path: '/extensions/promptclinic',
        icon: PromptClinicIcon,
        minLevel: 1,
        labelKey: 'nav.promptclinic',
      },
      {
        key: 'decicraft',
        path: '/extensions/decicraft',
        icon: DeciCraftIcon,
        minLevel: 1,
        labelKey: 'nav.decicraft',
      },
      {
        key: 'decisions',
        path: '/decisions',
        icon: DecisionIcon,
        minLevel: 1,
        labelKey: 'nav.decisions',
      }
    ],
    section: 'extensions'
  },
  {
    key: 'docs',
    path: '/docs',
    icon: DocsIcon,
    minLevel: 1,
    labelKey: 'nav.docs',
    section: 'docs'
  }
];

export const isAdminUser = (user) => {
  if (!user) return false;
  if (user.isAdmin === true) return true;
  return user.role === 'admin' || user.accessLevel === 'Level5';
};

export const getUserPlan = (user) => {
  if (!user) return 'free';
  if (user.plan === 'paid' || user.plan === 'free') return user.plan;
  if (isAdminUser(user)) return 'paid';
  return user.accessLevel && user.accessLevel !== 'Level1' ? 'paid' : 'free';
};

/** @deprecated 레거시 — accessLevel 숫자 */
export const getLevelNumber = (accessLevelOrUser) => {
  if (accessLevelOrUser && typeof accessLevelOrUser === 'object') {
    if (isAdminUser(accessLevelOrUser)) return 5;
    return getUserPlan(accessLevelOrUser) === 'paid' ? 2 : 1;
  }
  if (!accessLevelOrUser) return 1;
  const match = String(accessLevelOrUser).match(/Level(\d)/);
  return match ? parseInt(match[1]) : 1;
};

export const getLevelString = (levelNum) => {
  return `Level${Math.max(1, Math.min(5, levelNum))}`;
};

export const getLevelName = (levelOrUser) => {
  if (levelOrUser && typeof levelOrUser === 'object') {
    if (isAdminUser(levelOrUser)) return '플랫폼 관리자';
    return getUserPlan(levelOrUser) === 'paid' ? '스탠다드' : '무료';
  }
  const levelNum = typeof levelOrUser === 'number' ? levelOrUser : getLevelNumber(levelOrUser);
  const names = {
    1: '무료',
    2: '스탠다드',
    3: '관리자',
    4: '관리자',
    5: '플랫폼 관리자'
  };
  return names[levelNum] || '알 수 없음';
};

function menuVisible(menu, user) {
  if (menu.adminOnly) return isAdminUser(user);
  // 레거시 minLevel: 3+ → admin, 2 → paid|admin, 1 → all
  const min = menu.minLevel || 1;
  if (min >= 3) return isAdminUser(user);
  if (min === 2) return isAdminUser(user) || getUserPlan(user) === 'paid';
  return true;
}

export const getVisibleMenus = (accessLevelOrUser) => {
  const user = typeof accessLevelOrUser === 'object' && accessLevelOrUser
    ? accessLevelOrUser
    : { accessLevel: accessLevelOrUser };

  return menuItems.filter((menu) => menuVisible(menu, user));
};

export const getMenusBySection = (accessLevelOrUser, section) => {
  const user = typeof accessLevelOrUser === 'object' && accessLevelOrUser
    ? accessLevelOrUser
    : { accessLevel: accessLevelOrUser };

  const menus = getVisibleMenus(user).filter((menu) => menu.section === section);

  return menus.map((menu) => {
    if (menu.expandable && menu.children) {
      const filteredChildren = menu.children.filter((child) => menuVisible(child, user));
      return { ...menu, children: filteredChildren };
    }
    return menu;
  });
};

export const canAccessMenu = (menuKey, accessLevelOrUser) => {
  const menu = menuItems.find((m) => m.key === menuKey);
  if (!menu) return false;
  const user = typeof accessLevelOrUser === 'object' && accessLevelOrUser
    ? accessLevelOrUser
    : { accessLevel: accessLevelOrUser };
  return menuVisible(menu, user);
};

export const canWriteNotice = (user) =>
  isAdminUser(typeof user === 'object' ? user : { accessLevel: user });

export const canManageUsers = (user) => isAdminUser(typeof user === 'object' ? user : { accessLevel: user });

export const canManageSystem = (user) => isAdminUser(typeof user === 'object' ? user : { accessLevel: user });

export const canManagePlatform = (user) => isAdminUser(typeof user === 'object' ? user : { accessLevel: user });

export default {
  menuItems,
  isAdminUser,
  getUserPlan,
  getLevelNumber,
  getLevelString,
  getLevelName,
  getVisibleMenus,
  getMenusBySection,
  canAccessMenu,
  canWriteNotice,
  canManageUsers,
  canManageSystem,
  canManagePlatform,
};
